// Authentication Context
// Manages user authentication state across the entire app

import React, { createContext, useState, useContext, useEffect } from 'react';
import { apiUrl } from './utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state from localStorage on mount
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('doorbase_token');
      const storedUser = localStorage.getItem('doorbase_user');

      if (storedToken && storedUser) {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
      }

      // Small delay to ensure state is set before hiding loading
      await new Promise(resolve => setTimeout(resolve, 50));
      setLoading(false);
    };

    initAuth();
  }, []);

  // Login function
  const login = async (email, password) => {
    try {
      const response = await fetch(apiUrl('/api/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Store token and user in state and localStorage
      setToken(data.token);
      setUser(data.user);
      localStorage.setItem('doorbase_token', data.token);
      localStorage.setItem('doorbase_user', JSON.stringify(data.user));

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Call backend logout API
      if (token) {
        await fetch(apiUrl('/api/auth/logout'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear state and localStorage regardless of API success
      setToken(null);
      setUser(null);
      localStorage.removeItem('doorbase_token');
      localStorage.removeItem('doorbase_user');
    }
  };

  // Check if user has specific permission
  const hasPermission = (permissionKey) => {
    if (!user) return false;
    
    // Super admin has all permissions
    if (user.isSuperAdmin) return true;
    
    // Check if user has the specific permission
    return user.permissions && user.permissions.includes(permissionKey);
  };

  // Check if user has specific role
  const hasRole = (roleName) => {
    if (!user) return false;
    return user.roles && user.roles.includes(roleName);
  };

  // Check if token is expired (JWT expiry is 7 days)
  useEffect(() => {
    if (!token) return;

    // Parse JWT to get expiry
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiryTime = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();

      if (now >= expiryTime) {
        // Token expired, logout
        logout();
      } else {
        // Set timeout to logout when token expires
        const timeoutId = setTimeout(() => {
          logout();
        }, expiryTime - now);

        return () => clearTimeout(timeoutId);
      }
    } catch (error) {
      console.error('Error parsing token:', error);
      logout();
    }
  }, [token]);

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    hasPermission,
    hasRole,
    isAuthenticated: !!token && !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
